/**
 * The time-picking half of scheduling, shared by "Send later" and by editing a queued reply.
 *
 * One component so the two cannot drift: the presets, the Berlin wording and the "not in Gmail's
 * Scheduled folder" caveat are the same statement in both places, and a change to the preset times
 * lands in both. It owns no API call and no state beyond the value it is given.
 */
import { Input } from '@finn-auto/design-system/atoms/input';
import { Button } from '@finn-auto/design-system/atoms/button';
import { STEP_SECONDS, earliestSelectable, presetsFor } from './scheduleReplyTime';

export function ScheduleTimePicker({
  value,
  onChange,
  disabled,
  error,
}: {
  /** Berlin wall-clock, `YYYY-MM-DDTHH:mm` — the shape <input type="datetime-local"> wants. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  const presets = presetsFor();
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <Button
            key={p.key}
            size="sm"
            variant={value === p.value ? 'primary' : 'outline'}
            onClick={() => onChange(p.value)}
            disabled={disabled}
            data-no-toggle
          >
            {p.label}
          </Button>
        ))}
      </div>
      {/* Design-system Input, which supports datetime-local (see InterviewRescheduleButton). */}
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-700">
          Or pick a time (Berlin, 5-minute steps)
        </span>
        <Input
          id="ta-schedule-at"
          type="datetime-local"
          value={value}
          min={earliestSelectable()}
          step={STEP_SECONDS}
          onChange={(e) => onChange((e.target as HTMLInputElement).value)}
          disabled={disabled}
          aria-label="Send at (Berlin time)"
        />
      </label>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <p className="text-xs text-gray-500">
        Sent from your own mailbox at about that time. It does <strong>not</strong> appear in
        Gmail&apos;s &quot;Scheduled&quot; folder, so change or cancel it here rather than in Gmail.
        If the candidate writes again first, nothing is sent and the card comes back.
      </p>
    </div>
  );
}
